import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import { CircularProgress } from "@mui/material";
import toast from "react-hot-toast";
import { CustomModal } from "../../../../components/CustomModal/CustomModal";
import { CustomTextInput } from "../../../../components/CustomTextInput/CustomTextInput";
import { CustomTypography } from "../../../../components/CustomTypography/CustomTypography";
import { CustomButton } from "../../../../components/CustomButton/CustomButton";
import { Icon } from "../../../../components/Icon/Icon";
import { colors } from "../../../../styles/colors";
import { uploadToStorage } from "../../../../api/media";
import { useUpdateCompany } from "../../../../api/companies";
import { getCurrentFleetId } from "../../../../utils/getCurrentFleet";
import useStyles from "./styles";

type CompanyValues = {
  name: string;
  registration_id: string;
};

type Props = {
  open: boolean;
  company: TCompanie | undefined;
  onClose: () => void;
};

export const ModalUpdateCompany: FC<Props> = ({ open, company, onClose }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoUrl, setLogoUrl] = useState<string>("");
  const [uploading, setUploading] = useState(false);

  const { mutate: updateCompany, isPending } = useUpdateCompany(
    getCurrentFleetId()
  );

  const validationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .required(t("setting.companyNameRequired", "Company name is required")),
    registration_id: Yup.string().trim(),
  });

  const formik = useFormik<CompanyValues>({
    initialValues: {
      name: company?.name ?? "",
      registration_id: company?.registration_id ?? "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      updateCompany(
        {
          name: values.name.trim(),
          registration_id: values.registration_id.trim(),
          logo: logoUrl,
        },
        { onSuccess: () => onClose() }
      );
    },
  });

  // Reset form + logo each time the modal is (re)opened.
  useEffect(() => {
    if (open) {
      formik.resetForm();
      setLogoUrl(company?.logo ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, company?.id]);

  const onPickLogo = () => {
    if (uploading) return;
    fileInputRef.current?.click();
  };

  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t("setting.logoInvalidType", "Please select an image"));
      return;
    }
    setUploading(true);
    try {
      // "public/company", not "company": this URL is persisted on the company
      // row and rendered weeks later. The private namespace hands back a GET
      // signed with X-Amz-Expires=604800 — ~700 chars (which is what overflowed
      // the column) and dead after seven days. Only the "public/" prefix
      // carries the anonymous-read bucket policy, so its URL comes back
      // unsigned, short, permanent and cacheable.
      const { url } = await uploadToStorage(file, {
        directory: "public/company",
        filename: file.name,
      });
      setLogoUrl(url);
    } catch {
      toast.error(t("setting.logoUploadFailed", "Failed to upload logo"));
    } finally {
      setUploading(false);
    }
  };

  const logoChanged = logoUrl !== (company?.logo ?? "");
  const saveDisabled =
    (!formik.dirty && !logoChanged) || isPending || uploading;

  return (
    <CustomModal
      open={open}
      title={t("setting.editCompany", "Edit company")}
      onClose={onClose}
      onCancel={onClose}
      onSave={() => formik.handleSubmit()}
      loadingSave={isPending}
      disabledSaveButton={saveDisabled}
    >
      <div className={classes.containerText}>
        {/* Logo */}
        <div className={classes.logoRow}>
          <div className={classes.logoPreview}>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={t("setting.companyLogo", "Company logo") as string}
                className={classes.logoPreviewImg}
              />
            ) : uploading ? (
              <CircularProgress size={22} />
            ) : (
              <Icon
                name={"photo"}
                width={"28px"}
                height={"28px"}
                color={colors.greydark}
              />
            )}
          </div>
          <div className={classes.logoActions}>
            <CustomButton
              label={
                uploading
                  ? (t("common.loading", "Loading...") as string)
                  : logoUrl
                  ? (t("setting.changeLogo", "Change logo") as string)
                  : (t("setting.addLogo", "Add a logo") as string)
              }
              variant={"secondary"}
              height={"36px"}
              width={"fit-content"}
              onClick={onPickLogo}
            />
            {logoUrl && !uploading && (
              <CustomButton
                label={t("common.remove", "Remove") as string}
                variant={"secondary"}
                height={"36px"}
                width={"fit-content"}
                onClick={() => setLogoUrl("")}
              />
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className={classes.hiddenFileInput}
            onChange={onFileSelected}
          />
        </div>

        {/* Name */}
        <div>
          <CustomTextInput
            label={t("setting.companyName", "Company name")}
            placeholder={t("setting.companyName", "Company name")}
            marginBottom={"0px"}
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
          />
          {formik.touched.name && formik.errors.name && (
            <CustomTypography
              text={formik.errors.name}
              variant="link"
              styles={{ containerText: classes.errorText }}
            />
          )}
        </div>

        {/* Registration id (SIREN/SIRET) */}
        <div>
          <CustomTextInput
            label={t("setting.registrationId", "Registration number")}
            placeholder={t("setting.registrationId", "Registration number")}
            marginBottom={"0px"}
            name="registration_id"
            value={formik.values.registration_id}
            onChange={formik.handleChange}
          />
          {formik.touched.registration_id && formik.errors.registration_id && (
            <CustomTypography
              text={formik.errors.registration_id}
              variant="link"
              styles={{ containerText: classes.errorText }}
            />
          )}
        </div>
      </div>
    </CustomModal>
  );
};
